import React, { useEffect, useMemo, useState } from "react";
import {
  PiXLight,
  PiChecksLight,
  PiUsersThreeLight,
  PiWarningCircleLight,
} from "react-icons/pi";
import cx from "classnames";
import ProfileImage from "../Header/ProfileImage";
import Button from "./TailwindComponents/Button";
import SearchInput from "./TailwindComponents/SearchInput";
import { RootState, useAppDispatch } from "../../store";
import { fetchAllEmployees } from "../../store/actions/organization/employeeActions";
import { useSelector } from "react-redux";
import { getInitials } from "./TailwindComponents/CollaboratorsBadge";
import Dropdown, { DropdownOption } from "./TailwindComponents/Dropdown";
import { useTranslation } from "react-i18next";
import { normalise } from './access/accessModel';

export type AccessLevel = "view" | "edit" | "admin";
export interface ShareUser {
  id: string;
  name: string;
  email: string;
  initials: string;
  avatarUrl: string | null;
  role: string | string[];
  access?: AccessLevel;
}

interface Props {
  employees?: ShareUser[];
  initialShared: ShareUser[];
  onSave: (data: ShareUser[]) => Promise<void> | void;
  onCancel?: () => void;
  collaboratorRoles?: DropdownOption[];
}

const ShareAccessManager: React.FC<Props> = ({
  employees,
  initialShared,
  onSave,
  onCancel,
  collaboratorRoles
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<ShareUser[]>(initialShared);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const { employees: fetchedEmployees } = useSelector(
    (state: RootState) => state.employees
  );
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [fetchedPeople, setFetchedPeople] = useState<ShareUser[]>([]);

  useEffect(() => {
    // If employees prop is provided, use it directly
    if (fetchedEmployees && fetchedEmployees.length > 0) {
      setFetchedPeople(fetchedEmployees);
    } else {
      // Otherwise, fetch from Redux
      dispatch(fetchAllEmployees({ sortOrder: "desc" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedEmployees]);

  useEffect(() => {
    // Only transform fetchedEmployees if no employees prop was provided
    if (fetchedEmployees && fetchedEmployees.length > 0) {
      setFetchedPeople(
        fetchedEmployees
          .filter((f) => f.user_id !== userInfo?.UserID && f.first_name && f.last_name && f.email)
          .map((e) => {
            const firstName = e.first_name || "";
            const lastName = e.last_name || "";
            const name = `${firstName} ${lastName}`.trim();
            return {
              id: String(e.id),
              name: name || e.email || t("Unknown user"),
              email: e.email || "",
              role: e.role || "",
              // The directory row DOES carry a picture (`profile_picture` on
              // /org-people, `ProfilePicture` on the legacy employee shape).
              // This used to be hard-coded `null`, so every face in the
              // directory column fell straight through to Gravatar/initials
              // even for people whose photo the app already had.
              avatarUrl: normalise(e).avatar,
              initials: getInitials(name || e.email || ""),
            };
          })
      );
    }
  }, [fetchedEmployees, userInfo?.UserID, employees]);

  const selectedIds = useMemo(
    () => new Set(selected.map((u) => u.id)),
    [selected]
  );

  const filtered = useMemo(() => {
    if (!query) return fetchedPeople;
    const q = query.toLowerCase();
    return fetchedPeople.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q)
    );
  }, [fetchedPeople, query]);

  const toggle = (u: ShareUser) => {
    setSelected((prev) => {
      const exists = prev.find((x) => x.id === u.id);
      if (exists) return prev.filter((x) => x.id !== u.id);
      return [...prev, { ...u, access: u.access || "view", roles: [] }];
    });
  };

  const setAccess = (id: string, access: AccessLevel) => {
    setSelected((prev) =>
      prev.map((u) => (u.id === id ? { ...u, access } : u))
    );
  };

  const setRoles = (id: string, role: string | string[]) => {
    setSelected((prev) =>
      prev.map((u) => (u.id === id ? { ...u, role } : u))
    );
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(selected);
    } catch (e: any) {
      setError(e?.message || t("Failed to update sharing"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`relative flex flex-col justify-between w-full h-[60vh] overflow-hidden rounded-2xl bg-white dark:bg-gray-900  ${collaboratorRoles ? "md:min-w-[70vw]" : "md:min-w-[800px]"} md:max-w-[800px]`}>
      <div className="px-4 grid grid-cols-1 sm:grid-cols-5 gap-5">
        <div className={`${collaboratorRoles ? "sm:col-span-3" : "sm:col-span-2"}`}>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t("Selected")} ({selected.length})
          </p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pe-1 custom-scrollbar-main">
            {selected.length === 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("No one selected yet.")}
              </div>
            )}
            {selected.map((u) => (
              <div
                key={u.id}
                className="flex flex-col gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-800"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <ProfileImage
                      profilePicture={normalise(u).avatar}
                      email={u.email}
                      name={u.name}
                      initials={u.initials}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {u.name}
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                        {u.email}
                      </div>
                    </div>
                  </div>

                  <Dropdown
                    id="roles"
                    options={[
                      { label: t("Can view"), value: "view" },
                      { label: t("Can edit"), value: "edit" },
                    ]}
                    placeholder={t("Select access")}
                    onChange={(value) => setAccess(u.id, value as AccessLevel)}
                    value={u.access || "view"}
                    className="py-1"
                  />

                  {collaboratorRoles && collaboratorRoles.length > 0 && (
                    <Dropdown
                      id="role"
                      options={collaboratorRoles || []}
                      placeholder={t("Select role(s)")}
                      onChange={(value: string | string[]) => {
                        setRoles(u.id, value);
                      }}
                      value={u.role || []}
                    />
                  )}

                  <button
                    onClick={() => toggle(u)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    title={t("Remove")}
                    aria-label={t("Remove")}
                  >
                    <PiXLight className="h-4 w-4" />
                  </button>
                </div>

              </div>
            ))}
          </div>
        </div>

        <div className={`${collaboratorRoles ? "sm:col-span-2" : "sm:col-span-3"}`}>
          <div className="mb-2 mt-1">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder={t("Search people by name or email")}
            />
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
            {t("Directory")}
          </div>
          <div className="h-[45vh] overflow-y-auto pe-1 custom-scrollbar-main space-y-1">
            {filtered.map((u) => (
              <button
                key={u.id}
                onClick={() => toggle(u)}
                className={cx(
                  "w-full flex items-center justify-between gap-3 p-2 rounded-lg border",
                  selectedIds.has(u.id)
                    ? "border-primary-200 bg-primary-50/60 dark:bg-primary-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ProfileImage
                    profilePicture={normalise(u).avatar}
                    email={u.email}
                    name={u.name}
                    initials={u.initials}
                  />
                  <div className="min-w-0 text-start">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {u.name}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {u.email}
                    </div>
                  </div>
                </div>
                {selectedIds.has(u.id) ? (
                  <PiChecksLight className="h-5 w-5 text-primary-600" />
                ) : (
                  <PiUsersThreeLight className="h-5 w-5 text-gray-400" />
                )}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("No matches found.")}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="px-5 -mt-2 text-xs text-danger-600 dark:text-danger-400 flex items-center gap-2">
          <PiWarningCircleLight className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
        {onCancel && (
          <Button
            onClick={() => {
              onCancel();
              setError(null);
            }}
            variant="neutral"
            size="small"
          >
            {t("Cancel")}
          </Button>
        )}
        <Button onClick={save} disabled={saving} variant="primary" size="small">
          {saving ? t("Saving...") : t("Save changes")}
        </Button>
      </div>
    </div>
  );
};

export default ShareAccessManager;
